import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const campaignFormSchema = z.object({
  name: z.string().min(2, {
    message: "Campaign name must be at least 2 characters.",
  }),
  budget_usd: z.coerce.number().min(1000, {
    message: "Minimum budget is $1,000",
  }),
  campaign_start_date: z.string().refine((date) => new Date(date) > new Date(), {
    message: "Start date must be in the future",
  }),
  campaign_end_date: z.string(),
  target_audience: z.string().min(10, "Please describe your target audience"),
  target_categories: z.array(z.string()).min(1, "Select at least one category"),
  suitability_filters: z.array(z.string()),
  geographic_targets: z.string().min(2, "Please specify geographic targets"),
  success_metrics: z.string().min(10, "Please describe success metrics"),
  integration_preferences: z.array(z.string()).min(1, "Select at least one integration type"),
  // The three fields O3's matcher needs that nothing else on this form captures. Optional, because
  // a campaign is still a valid campaign without them — they sharpen matching rather than gate it.
  product_terms: z.string().optional(),
  target_iab: z.string().optional(),
  match_description: z.string().optional(),
}).refine((data) => new Date(data.campaign_end_date) > new Date(data.campaign_start_date), {
  message: "End date must be after start date",
  path: ["campaign_end_date"],
});

type CampaignFormValues = z.infer<typeof campaignFormSchema>;

interface RequirementsFormProps {
  windowId: string;
  onSuccess?: () => void;
}

const CATEGORIES = [
  "Arts & Entertainment",
  "Automotive",
  "Business",
  "Careers",
  "Education",
  "Family & Parenting",
  "Food & Drink",
  "Health & Fitness",
  "Hobbies & Interests",
  "Home & Garden",
  "Law, Gov't & Politics",
  "News",
  "Personal Finance",
  "Pets",
  "Science",
  "Society",
  "Sports",
  "Style & Fashion",
  "Technology & Computing",
  "Travel"
];

const INTEGRATION_TYPES = [
  { id: "logo_placement", label: "Logo Placement" },
  { id: "product_placement", label: "Product Placement" },
  { id: "verbal_mention", label: "Verbal Mention" },
  { id: "dedicated_segment", label: "Dedicated Segment" },
  { id: "social_shares", label: "Social Shares" }
];

const SUITABILITY_FILTERS = [
  { id: "no_alcohol", label: "No Alcohol" },
  { id: "no_gambling", label: "No Gambling" },
  { id: "no_profanity", label: "No Profanity" },
  { id: "family_friendly", label: "Family Friendly Only" },
  { id: "g_rated", label: "G-Rated Content" }
];

// The suitability checkboxes above already say what a brand will not sit beside. The matcher wants
// the same thing in GARM buckets, so translate rather than asking twice — a brand that ticks
// "No Alcohol" and then omits it from a second list has contradicted itself, and nothing would say
// which answer won.
const EXCLUSIONS_FROM_FILTERS = (filters: string[]): string =>
  [
    filters.includes("no_alcohol") && "Alcohol",
    filters.includes("no_gambling") && "Gambling",
  ]
    .filter(Boolean)
    .join("; ");

// Likewise the ceiling. "G-Rated" is the strictest thing this form can express and TV-PG is the
// strictest the matcher understands, so both map there. No selection means NO ceiling, deliberately:
// the gate should only remove inventory a brand actually asked to remove, and silence is not a
// request.
const RATING_FROM_FILTERS = (filters: string[]): string =>
  filters.includes("g_rated") || filters.includes("family_friendly") ? "TV-PG" : "";

export function RequirementsForm({ windowId, onSuccess }: RequirementsFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      name: "",
      budget_usd: 0,
      campaign_start_date: "",
      campaign_end_date: "",
      target_audience: "",
      target_categories: [],
      suitability_filters: [],
      geographic_targets: "",
      success_metrics: "",
      integration_preferences: [],
      product_terms: "",
      target_iab: "",
      match_description: "",
    },
  });

  // The options come from the SERVER, not a list in this file. The category list above
  // (CATEGORIES) is the older IAB content taxonomy and not one of its twenty entries resolves
  // against the inventory's own catalogue — a brief built from it would rank confidently with its
  // strongest semantic signal silently switched off. Reading them from /brand-match/fields means
  // the form can only ever offer values the matcher can act on.
  const [iabOptions, setIabOptions] = useState<string[]>([]);
  useEffect(() => {
    apiClient
      .get<{ target_iab: string[] }>("/api/v1/brand-match/fields")
      .then((r) => setIabOptions(r.target_iab || []))
      // A failure here costs the dropdown, not the form: the other fields still submit and the
      // campaign is still created. Better a missing optional field than a blocked submission.
      .catch(() => setIabOptions([]));
  }, []);

  async function onSubmit(data: CampaignFormValues) {
    setIsSubmitting(true);
    try {
      const campaignData = {
        name: data.name,
        window_id: windowId,
        requirements: {
          ...data,
          target_audience: [data.target_audience], // Convert to array as expected by backend
          geographic_targets: [data.geographic_targets], // Convert to array
          success_metrics: [data.success_metrics], // Convert to array
          // The brief the placement matcher reads, assembled here rather than reconstructed later.
          // Two of its fields already exist on this form under other names — the suitability filters
          // ARE the exclusions, and asking for G-rated content IS a rating ceiling — so deriving
          // them here means a brand states each thing once and cannot contradict itself.
          brief: {
            brand: data.name,
            product_terms: data.product_terms || "",
            target_iab: data.target_iab || "",
            match_description: data.match_description || "",
            excluded_categories: EXCLUSIONS_FROM_FILTERS(data.suitability_filters),
            rating_ceiling: RATING_FROM_FILTERS(data.suitability_filters),
            buy_type: "content",
          },
        }
      };

      await apiClient.createCampaign(campaignData);
      
      toast({
        title: "Requirements Submitted",
        description: "Your campaign requirements have been received.",
      });

      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/brand-dashboard");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit requirements",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Campaign Requirements</CardTitle>
        <CardDescription>
          Tell us about your campaign needs for this booking window.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Campaign Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Summer Launch 2026" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="budget_usd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Budget (USD)</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormDescription>Minimum $1,000</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="campaign_start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="campaign_end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="target_categories"
              render={() => (
                <FormItem>
                  <FormLabel>Target Categories</FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {CATEGORIES.map((category) => (
                      <FormField
                        key={category}
                        control={form.control}
                        name="target_categories"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={category}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(category)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, category])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== category
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">
                                {category}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="integration_preferences"
              render={() => (
                <FormItem>
                  <FormLabel>Integration Preferences</FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                    {INTEGRATION_TYPES.map((type) => (
                      <FormField
                        key={type.id}
                        control={form.control}
                        name="integration_preferences"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={type.id}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(type.id)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, type.id])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== type.id
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">
                                {type.label}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="suitability_filters"
              render={() => (
                <FormItem>
                  <FormLabel>Suitability & Brand Safety</FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                    {SUITABILITY_FILTERS.map((filter) => (
                      <FormField
                        key={filter.id}
                        control={form.control}
                        name="suitability_filters"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={filter.id}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(filter.id)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, filter.id])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== filter.id
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">
                                {filter.label}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-6">
              <FormField
                control={form.control}
                name="target_audience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Audience Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe your ideal customer demographics, interests, and behaviors..."
                        className="min-h-[100px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="geographic_targets"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Geographic Targets</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. US, UK, Canada, or specific regions" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="success_metrics"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Success Metrics (KPIs)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="What does success look like? e.g. View count, CPM target, conversions..."
                        className="min-h-[80px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting Requirements...
                </>
              ) : (
                "Submit Campaign Requirements"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
